// TanStack Query keys and hooks for the inbox views: labels per account (refreshed once when a
// picker opens), Gmail search results, and the work item a thread action queued (followed by the
// `work` SSE event; polling is the safety net).
import { queryOptions, useQueries, useQuery, type QueryClient } from "@tanstack/react-query";
import type { LabelView, WorkView } from "@shared/api";
import { inboxApi } from "./inboxApi";

export const inboxKeys = {
  labels: (accountId: string) => ["labels", accountId] as const,
  gmailSearch: (accountId: string, q: string) => ["gmailSearch", accountId, q.trim()] as const,
  work: (workId: string) => ["threadWork", workId] as const,
  receipts: (workId: string) => ["threadWork", workId, "receipts"] as const,
};

export function labelsQuery(accountId: string) {
  return queryOptions({
    queryKey: inboxKeys.labels(accountId),
    queryFn: ({ signal }) => inboxApi.labels(accountId, false, signal),
    staleTime: 5 * 60_000,
  });
}

export function useLabels(accountId: string | null) {
  return useQuery({ ...labelsQuery(accountId ?? ""), enabled: !!accountId });
}

/** The label lists of several accounts (the rail draws one tree per account). */
export function useAccountLabels(accountIds: readonly string[]) {
  return useQueries({
    queries: accountIds.map((id) => labelsQuery(id)),
    combine: (results) => ({
      byAccount: new Map(
        accountIds.map((id, i) => [id, results[i]?.data ?? null] as [string, LabelView[] | null]),
      ),
      isPending: results.some((r) => r.isPending),
    }),
  });
}

/** Ask Gmail for the label list once (1 quota unit) and put it in the cache; best effort. */
export async function refreshLabels(queryClient: QueryClient, accountId: string): Promise<void> {
  try {
    const fresh = await inboxApi.labels(accountId, true);
    queryClient.setQueryData(inboxKeys.labels(accountId), fresh);
  } catch {
    // The cached list stays on screen; the picker says nothing about it.
  }
}

/** Every label cached for these accounts, for naming ids in receipts and previews. */
export function labelIndex(
  lists: Iterable<LabelView[] | null | undefined>,
): Map<string, LabelView> {
  const out = new Map<string, LabelView>();
  for (const list of lists) for (const label of list ?? []) out.set(label.id, label);
  return out;
}

export function useGmailSearch(accountId: string | null, q: string) {
  return useQuery({
    queryKey: inboxKeys.gmailSearch(accountId ?? "", q),
    queryFn: ({ signal }) => inboxApi.gmailSearch(accountId ?? "", q, signal),
    enabled: !!accountId && q.trim().length > 0,
    retry: false,
    staleTime: 60_000,
  });
}

const TERMINAL: ReadonlySet<WorkView["status"]> = new Set([
  "completed",
  "failed",
  "cancelled",
  "stale",
]);

export function isTerminalWork(work: Pick<WorkView, "status"> | null | undefined): boolean {
  return !!work && TERMINAL.has(work.status);
}

/** A work item by id; polls every 5 s until it settles (the SSE event usually gets there first). */
export function useWorkItem(workId: string | null) {
  return useQuery({
    queryKey: inboxKeys.work(workId ?? ""),
    queryFn: ({ signal }) => inboxApi.work(workId ?? "", signal),
    enabled: !!workId,
    staleTime: 2_000,
    refetchInterval: (query) => (isTerminalWork(query.state.data) ? false : 5_000),
  });
}

export function useWorkReceipts(workId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: inboxKeys.receipts(workId ?? ""),
    queryFn: ({ signal }) => inboxApi.workReceipts(workId ?? "", signal),
    enabled: enabled && !!workId,
    staleTime: 60_000,
  });
}

export function invalidateThreadWork(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ["threadWork"] });
}
